"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PriceAdvocateRoadmap() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: stepsRef,
    isVisible: stepsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: noteRef,
    isVisible: noteVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.3
  });
  const steps = [{
    number: "01",
    title: "Initial Consultation",
    description: "We discuss your upcoming purchase and identify the specific leverage points for your negotiation.",
    duration: "20 minutes"
  }, {
    number: "02",
    title: "Market Analysis",
    description: "Our team researches current pricing trends and competitor offers to build your winning strategy.",
    duration: "24 hours"
  }, {
    number: "03",
    title: "Strategic Negotiation",
    description: "We engage directly with vendors on your behalf to secure the best possible terms and price.",
    duration: "1-3 days"
  }, {
    number: "04",
    title: "Final Review",
    description: "We present the final agreement for your approval, ensuring every detail meets your expectations.",
    duration: "Final Step"
  }];
  return <section data-section-id="5100" id="the-path-to-savings" className="bg-white py-20 md:py-28 lg:py-32 overflow-hidden">
      <style>{`
        @keyframes ecg-pulse-travel {
          0% { left: 0%; opacity: 0; }
          5% { opacity: 1; }
          95% { opacity: 1; }
          100% { left: 100%; opacity: 0; }
        }
        @keyframes ecg-pulse-travel-vertical {
          0% { top: 0%; opacity: 0; }
          5% { opacity: 1; }
          95% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes ecg-draw {
          from { stroke-dashoffset: 1000; }
          to { stroke-dashoffset: 0; }
        }
        @keyframes ecg-draw-vertical {
          from { stroke-dashoffset: 800; }
          to { stroke-dashoffset: 0; }
        }
        .ecg-line-draw {
          stroke-dasharray: 1000;
          stroke-dashoffset: 1000;
          animation: ecg-draw 2s ease-out forwards;
        }
        .ecg-line-draw-vertical {
          stroke-dasharray: 800;
          stroke-dashoffset: 800;
          animation: ecg-draw-vertical 2s ease-out forwards;
        }
        .pulse-dot-horizontal {
          animation: ecg-pulse-travel 4s linear infinite;
          animation-delay: 1.8s;
        }
        .pulse-dot-vertical {
          animation: ecg-pulse-travel-vertical 4s linear infinite;
          animation-delay: 1.8s;
        }
        @keyframes soft-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.4); opacity: 0.7; }
        }
        .soft-pulse {
          animation: soft-pulse 1.5s ease-in-out infinite;
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-20 md:mb-24 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <p className="font-default text-xs md:text-sm tracking-[0.2em] uppercase text-[#e87a27] mb-6 font-bold">
              — Our Process
            </p>
            <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[64px] leading-[1.05] tracking-tight text-[#1a3a6b] uppercase">
              <span className="inline-block w-3 h-3 bg-[#e87a27] mr-3 align-middle"></span>
              Your path to
              <br />
              <span className="font-bold">better pricing.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-8">
            <p className="font-default text-base md:text-lg leading-relaxed text-[#333333]">
              We simplify the complex world of high-stakes negotiations. Our proven four-step framework ensures you never overpay for your most important purchases again.
            </p>
          </div>
        </div>

        <div ref={stepsRef} className="relative">
          <div className="hidden md:block absolute left-0 right-0 top-[88px] pointer-events-none">
            <div className="relative w-full h-[2px]">
              <svg className="w-full h-[20px] absolute -top-[10px] left-0" viewBox="0 0 1000 20" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 10 L200 10 L210 10 L215 4 L220 16 L225 10 L1000 10" stroke="#cccccc" strokeWidth="1" className={stepsVisible ? "ecg-line-draw" : ""} style={{
                strokeDashoffset: stepsVisible ? undefined : 1000
              }} />
              </svg>
              {stepsVisible && <div className="absolute -top-[5px] pulse-dot-horizontal">
                  <div className="relative w-3 h-3">
                    <div className="absolute inset-0 rounded-lg bg-[#e87a27] soft-pulse"></div>
                    <div className="absolute inset-[3px] rounded-lg bg-[#e87a27]"></div>
                  </div>
                </div>}
            </div>
          </div>

          <div className="md:hidden absolute left-[28px] top-0 bottom-0 pointer-events-none">
            <div className="relative h-full w-[2px]">
              <svg className="h-full w-[20px] absolute -left-[10px] top-0" viewBox="0 0 20 800" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 0 L10 200 L10 210 L4 215 L16 220 L10 225 L10 800" stroke="#cccccc" strokeWidth="1" className={stepsVisible ? "ecg-line-draw-vertical" : ""} style={{
                strokeDashoffset: stepsVisible ? undefined : 800
              }} />
              </svg>
              {stepsVisible && <div className="absolute -left-[5px] pulse-dot-vertical">
                  <div className="relative w-3 h-3">
                    <div className="absolute inset-0 rounded-lg bg-[#e87a27] soft-pulse"></div>
                    <div className="absolute inset-[3px] rounded-lg bg-[#e87a27]"></div>
                  </div>
                </div>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-8 lg:gap-8 relative">
            {steps.map((step, i) => <div key={i} data-index={i} className={`relative pl-16 md:pl-0 transition-all duration-700 ease-out ${stepsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: stepsVisible ? `${i * 100}ms` : "0ms"
          }}>
                <div className="absolute md:relative left-[22px] md:left-auto top-[10px] md:top-auto md:mb-8 md:flex md:items-center md:justify-start">
                  <div className="hidden md:block w-3 h-3 rounded-lg bg-white border-2 border-[#1a3a6b] relative z-10"></div>
                  <div className="md:hidden w-3 h-3 rounded-lg bg-white border-2 border-[#1a3a6b] relative z-10"></div>
                </div>

                <div className="font-default font-bold text-5xl md:text-[56px] leading-none text-[#1a3a6b] mb-5">
                  {step.number}
                </div>

                <h3 className="font-default text-[22px] leading-snug font-bold text-[#1a3a6b] mb-3 tracking-tight uppercase">
                  {step.title}
                </h3>

                <p className="font-default text-sm leading-relaxed text-[#333333] mb-5">
                  {step.description}
                </p>

                <div className="inline-flex items-center gap-2">
                  <span className="w-4 h-px bg-[#e87a27]"></span>
                  <span className="font-default text-xs tracking-[0.15em] uppercase text-[#e87a27] font-bold">
                    {step.duration}
                  </span>
                </div>
              </div>)}
          </div>
        </div>

        <p ref={noteRef} className={`font-default text-center text-base md:text-lg text-[#333333] mt-20 md:mt-24 max-w-2xl mx-auto transition-all duration-700 ease-out ${noteVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          We are your dedicated partners in every negotiation, ensuring you always have the upper hand.
        </p>
      </div>
    </section>;
}